/**
 * Classifier for IndexedDB errors caused by iOS/WebKit suspending the app (CC-1864).
 *
 * When the app is backgrounded, WebKit aborts in-flight IndexedDB transactions. Any
 * Dexie operation mid-flight rejects with one of a small family of DOMException shapes.
 * This module recognizes that family so callers can distinguish "expected suspension
 * abort" from a genuine database failure (corruption, quota, constraint violations —
 * none of which match here).
 *
 * Pure module by design: no Sentry/Capacitor/Dexie imports, so it is unit-testable and
 * reusable from UI hooks (e.g. useGetLocalData) without pulling in the logging layer.
 */

interface ErrorLike {
  name?: string;
  message?: string;
  inner?: unknown;
  failures?: unknown[];
}

/**
 * Dexie wraps some failures: ModifyError/BulkError carry the underlying errors in
 * `failures`, other DexieErrors carry `inner`. Unwrap so name/message reflect the
 * underlying IndexedDB DOMException. Depth-capped so a pathological self-referencing
 * error chain cannot recurse forever.
 */
export function unwrapDexieError(error: unknown, depth = 5): unknown {
  const e = error as ErrorLike | null | undefined;
  if (depth <= 0 || !e || typeof e !== 'object') return error;
  if (e.inner && e.inner !== error) return unwrapDexieError(e.inner, depth - 1);
  if (Array.isArray(e.failures) && e.failures.length > 0 && e.failures[0] !== error)
    return unwrapDexieError(e.failures[0], depth - 1);
  return error;
}

/**
 * Message fragments observed in production for suspension aborts (Sentry issues
 * OTUVY-FRONTLINE-5, -H, -B). All are WebKit's transaction-lifecycle failures; none
 * are emitted for data-level errors.
 */
const SUSPENSION_MESSAGE_FRAGMENTS = [
  'Transaction aborted',
  "cursor that doesn't exist",
  'without an in-progress transaction',
  'Connection to Indexed Database server lost',
];

/**
 * Whether an error looks like an iOS-suspension IndexedDB abort.
 *
 * Aligned with Dexie's own benign-transient list: liveQuery internally swallows
 * AbortError and DatabaseClosedError (dexie.js liveQuery error branch) — we extend that
 * with the UnknownError message family WebKit uses for the same underlying aborts.
 *
 * AbortError additionally requires the 'Transaction aborted' message so an aborted
 * fetch() (also an AbortError, message "Fetch is aborted"/"The operation was aborted")
 * is never classified as a DB suspension.
 */
export function isTransientDbSuspensionError(error: unknown): boolean {
  const err = unwrapDexieError(error) as ErrorLike | null | undefined;
  if (!err || typeof err !== 'object') return false;
  const name = err.name ?? '';
  const message = err.message ?? '';
  if (name === 'AbortError') return message.includes('Transaction aborted');
  if (name === 'DatabaseClosedError') return true;
  if (name === 'UnknownError') return SUSPENSION_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
  return false;
}
